import React, { useState, useEffect, useRef } from "react";
import database from "../config/FirbaseConfig";

const normalizeCart = (cart = []) => {
  const map = {};

  cart.forEach((item) => {
    // Create a comparison key excluding Qty & Amount
    const { Qty, Amount, ...comparable } = item;

    const key = JSON.stringify({
      ...comparable,
      SelectedAddOns: [...(item.SelectedAddOns || [])].sort((a, b) =>
        (a?.Name || "").localeCompare(b?.Name || "")
      ),
    });

    if (!map[key]) {
      map[key] = {
        ...item,
        Qty: item.Qty,
        Amount: 0,
      };
    } else {
      map[key].Qty += item.Qty;
    }
  });

  // Recalculate Amount correctly
  return Object.values(map).map((item) => {
    let amount = item.Price * item.Qty;

    if (item.SelectedAddOns?.length) {
      item.SelectedAddOns.forEach((addon) => {
        if (addon?.Price) {
          amount += addon.Price * item.Qty;
        }
      });
    }

    return {
      ...item,
      Amount: amount,
    };
  });
};

const Cart = ({ restaurantID, cart, setCart, isOpen, setIsOpen }) => {
  const phoneNumber = JSON.parse(localStorage.getItem("loggedInUser"))?.['phoneDigits'];

  const totalAmount = cart.reduce((sum, item) => sum + (item.Amount || 0), 0);
  const updatingRef = useRef(false);

  console.log("Cart Rendered with items:", cart);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (updatingRef.current) return;

    const normalized = normalizeCart(cart);

    if (normalized.length !== cart.length) {
      updatingRef.current = true;
      setCart(normalized);
      localStorage.setItem(`${restaurantID}_Cart`, JSON.stringify(normalized));
      // Reset AFTER React finishes updating
      setTimeout(() => {
        updatingRef.current = false;
      }, 0);
    }
  }, [cart, restaurantID]);

  const increaseQty = (index) => {
    const updatedItems = [...cart];
    updatedItems[index].Qty += 1;
    let Amount = updatedItems[index]?.Price * updatedItems[index]?.Qty;
    for (let i = 0; i < updatedItems[index]?.SelectedAddOns.length; i++) {
      if (updatedItems[index]?.SelectedAddOns[i]?.Price) {
        Amount +=
          updatedItems[index]?.SelectedAddOns[i]?.Price *
          updatedItems[index]?.Qty;
      }
    }
    updatedItems[index].Amount = Amount;
    setCart(updatedItems);
    localStorage.setItem(`${restaurantID}_Cart`, JSON.stringify(updatedItems));
  };

  const decreaseQty = (index) => {
    const updatedItems = [...cart];
    if (updatedItems[index].Qty > 1) {
      updatedItems[index].Qty -= 1;
      let Amount = updatedItems[index]?.Price * updatedItems[index]?.Qty;
      for (let i = 0; i < updatedItems[index]?.SelectedAddOns.length; i++) {
        if (updatedItems[index]?.SelectedAddOns[i]?.Price) {
          Amount +=
            updatedItems[index]?.SelectedAddOns[i]?.Price *
            updatedItems[index]?.Qty;
        }
      }
      updatedItems[index].Amount = Amount;
    } else {
      updatedItems.splice(index, 1); // Remove item if Qty becomes 0
    }
    setCart(updatedItems);
    localStorage.setItem(`${restaurantID}_Cart`, JSON.stringify(updatedItems));
  };

  const placeOrder = async () => {
    try {
      const requestID = Date.now();
      const tableNo = localStorage.getItem(`${restaurantID}_Table_No`) || null;

      if (!tableNo) {
        alert("Table number not found. Please Rescan the QR code.");
        return;
      }

      if (!phoneNumber) {
        alert("Login to add to cart.");
        window.location.reload();
        return;
      }

      // User profile is already fetched by context
      const user = database.ref(`Users/${phoneNumber.replace("+", "")}`).once("value").then(snap => snap.exists() ? snap.val() : null);

      if (!user) {
        // This case might happen if user is logged in (phoneNumber exists) but no DB profile exists yet.
        // In that case, we should probably prompt them or create a default one.
        // Based on existing logic:
        const phoneKey = phoneNumber.replace("+", "");
        const userRef = database.ref(`Users/${phoneKey}`);

        await userRef.set({
          phone: phoneNumber,
          gender: "",
          email: "",
          userId: phoneNumber, // Fallback
          createdAt: Date.now(),
          createdAtLocal: new Date()
            .toLocaleString("sv-SE", {
              timeZone: "Asia/Kolkata",
              hour12: false,
            })
            .replace("T", " "),
        });
        alert(
          "Kindly save your name in Profile section below, before placing order."
        );
        return;
      }
      const OrderInfo = {
        order: cart,
        requestID: requestID,
        tableNo: tableNo,
        status: "processing",
        user: { name: user?.name || "", phone: user?.phone || "" },
        createdOn: Date.now(),
        createdOnLocal: new Date()
          .toLocaleString("sv-SE", { timeZone: "Asia/Kolkata", hour12: false })
          ?.replace("T", " "),
      };
      await database.ref(`Orders/${requestID}`).set(OrderInfo);
      await database
        .ref(`Users/${phoneNumber.replace("+", "")}/OrderHistory/${requestID}`)
        .set(OrderInfo);

      setCart([]);
      localStorage.setItem(
        `${restaurantID}_ThankYouPage_OrderInfo`,
        JSON.stringify(OrderInfo)
      );

      await window.gtag("event", `Order_Placed_Cart`, OrderInfo);

      localStorage.setItem(`${restaurantID}_Cart`, JSON.stringify([]));
      setIsOpen(false);
      window.location.href = "/ThankYouOrderPlaced";
    } catch (error) {
      console.error("Error placing order:", error);
      alert("Failed to place order. Please try again.");
    }
  };

  return (
    <>
      {isOpen ? (
        <div
          style={{
            position: "fixed",
            top: "8vh",
            left: "0vw",
            height: "92vh",
            width: "100vw",
            backgroundColor: "#FFFFFF",
            zIndex: 1000,
            borderRadius: "25px 25px 0px 0px",
            boxShadow: "2px 5px 15px #4F3C2B75",
            padding: "75px 15px",
            overflow: "hidden",
            overflowY: "scroll",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "2.5vh",
              right: "2.25vh",
              fontSize: "36px",
              fontWeight: "400",
              width: "36px",
              height: "36px",
              lineHeight: "12px",
              padding: "0px",
              textAlign: "center",
              color: "#000000",
              backgroundColor: "#ffffff",
              borderRadius: "100%",
              boxShadow: "1px 1px 10px #001E3A25",
              textShadow: "1px 1px 10px #001E3A25",
              zIndex: 1,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={(e) => {e.stopPropagation(); setIsOpen(false);}}
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#00000"><path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z"/></svg>
          </div>

          {/* <div style={{ position: 'absolute', top: '12px', right: '15px', color: '#ccc', textAlign: 'right', fontSize: '12px', lineHeight: '32px' }}>we use Cookies*</div> */}

          <div
            style={{
              textAlign: "left",
              fontSize: "14px",
              lineHeight: "20px",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (
                window.confirm("Are you sure you want to clear the wishlist?")
              ) {
                localStorage.setItem(
                  `${restaurantID}_Cart`,
                  JSON.stringify([])
                );
                setCart([]);
                setIsOpen(false);
                window.location.reload();
              }
            }}
          >
            Clear
          </div>
          {cart.length > 0 ? (
            cart
              .sort((b, a) => a?.["Image"].localeCompare(b?.["Image"]))
              .map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "start",
                    borderBottom: "1px solid #ccc",
                    padding: "15px 5px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: "50vw",
                      minHeight: "35vw",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      alignItems: "start",
                    }}
                  >
                    <div
                      style={{
                        width: "48vw",
                        fontSize: "18px",
                        lineHeight: "20px",
                        fontWeight: "bold",
                        color: "#333",
                        textAlign: "left",
                      }}
                    >
                      {item.Variant ? item.Variant : item.Name}
                    </div>
                    {item?.SelectedAddOns && item.SelectedAddOns.length > 0 && (
                      <div
                        style={{
                          fontSize: "12px",
                          lineHeight: "14px",
                          color: "#555",
                          margin: "3px 0px",
                        }}
                      >
                        <strong>AddOns:</strong>{" "}
                        {item?.SelectedAddOns?.map((addon) => addon.Name).join(
                          ", "
                        )}
                      </div>
                    )}
                    {item?.AdditionalNotes && (
                      <div
                        style={{
                          fontSize: "12px",
                          lineHeight: "14px",
                          color: "#555",
                          margin: "3px 0px",
                        }}
                      >
                        <strong>Additional Note:</strong>{" "}
                        {item?.AdditionalNotes}
                      </div>
                    )}
                    {item?.Qty && item?.Price && (
                      <div
                        style={{
                          fontSize: "12px",
                          lineHeight: "14px",
                          color: "#555",
                          margin: "3px 0px 1px 0px",
                        }}
                      >
                        <strong>Qty:</strong> {item.Qty}&nbsp;&nbsp;
                        <strong>Price:</strong> {item.Price}
                      </div>
                    )}
                    {item?.Amount && (
                      <div
                        style={{
                          fontSize: "12px",
                          lineHeight: "14px",
                          color: "#555",
                          margin: "1px 0px 5px 0px",
                        }}
                      >
                        <strong>Amount:</strong> {item.Amount}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: "auto",
                        display: "grid",
                        gridTemplateColumns: "10vw 10vw 10vw",
                        width: "30vw",
                        height: "35px",
                        boxShadow: "inset 1px 1px 5px #001E3A25",
                        borderRadius: "20px",
                        alignItems: "center",
                        textAlign: "center",
                        fontSize: "18px",
                        lineHeight: "35px",
                        fontWeight: 600,
                        color: "#001E3A",
                      }}
                    >
                      <div
                        onClick={(e) => {e.stopPropagation(); decreaseQty(index);}}
                        style={{ cursor: "pointer" }}
                      >
                        &#45;
                      </div>
                      <div>{item.Qty}</div>
                      <div
                        onClick={(e) => {e.stopPropagation(); increaseQty(index);}}
                        style={{ cursor: "pointer" }}
                      >
                        &#x2B;
                      </div>
                    </div>
                  </div>
                  {item["Image"] &&
                    item["Image"] !== "" &&
                    item["Image"] !== null &&
                    item["Image"] !== "nan" && (
                      <div
                        className="WishlistItemImage"
                        style={{
                          width: "35vw",
                          height: "35vw",
                          overflow: "hidden",
                          objectFit: "cover",
                          borderRadius: "15px",
                        }}
                      >
                        <img
                          src={item["Image"]}
                          alt={item.Variant ? item.Variant : item.Name}
                          style={{
                            width: "35vw",
                            height: "35vw",
                            overflow: "hidden",
                            objectFit: "cover",
                            borderRadius: "15px",
                          }}
                        />
                      </div>
                    )}
                </div>
              ))
          ) : (
            <div>Your wishlist is empty</div>
          )}
          <div
            style={{
              margin: "15px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "18px",
              color: "#001E3A",
            }}
          >
            <span>Total:</span>
            <strong>{totalAmount}</strong>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "18px",
              color: "#001E3A",
            }}
          >
            <button
              onClick={(e) => {e.stopPropagation(); placeOrder();}}
              style={{
                padding: "15px 25px",
                width: "85vw",
                borderRadius: "25px",
                border: "none",
                backgroundColor: "#001E3A",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Place Order
            </button>
          </div>
        </div>
      ) : (
        cart.length > 0 && (
          <>
            <div
              className="upanddown"
              style={{
                position: "fixed",
                top: "25vh",
                right: "15px",
                transform: "translateX(0%)",
                width: "60px",
                height: "60px",
                backgroundColor: "#001E3ADC",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4.5vh",
                boxShadow: "inset 0px 0px 5vh #001E3A, 0px 0px 5vh #FFFFFF50",
                fontSize: "21px",
                fontWeight: 600,
                lineHeight: "7vh",
                color: "#FFFFFF",
                textShadow: "0px 0px 5vh #001E3A, 0px 0px 5px #001E3A",
                overflow: "hidden",
                zIndex: 900,
              }}
              onClick={(e) => {e.stopPropagation(); setIsOpen(!isOpen);}}
            >
              <div
                style={{
                  fontSize: "14px",
                  position: "absolute",
                  top: "-7px",
                  left: "52%",
                  transform: "translateX(-50%)",
                  textAlign: "center",
                }}
              >
                {cart.length}
              </div>
              <div style={{ position: "absolute", top: "5px", left: "15px" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="27"
                  viewBox="0 0 46 42"
                  fill="none"
                >
                  <path
                    d="M11.9395 41.1C10.9465 41.1 10.1 40.7465 9.4 40.0395C8.7 39.3325 8.35 38.4825 8.35 37.4895C8.35 36.4965 8.7035 35.65 9.4105 34.95C10.1175 34.25 10.9675 33.9 11.9605 33.9C12.9535 33.9 13.8 34.2535 14.5 34.9605C15.2 35.6675 15.55 36.5175 15.55 37.5105C15.55 38.5035 15.1965 39.35 14.4895 40.05C13.7825 40.75 12.9325 41.1 11.9395 41.1ZM36.1395 41.1C35.1465 41.1 34.3 40.7465 33.6 40.0395C32.9 39.3325 32.55 38.4825 32.55 37.4895C32.55 36.4965 32.9035 35.65 33.6105 34.95C34.3175 34.25 35.1675 33.9 36.1605 33.9C37.1535 33.9 38 34.2535 38.7 34.9605C39.4 35.6675 39.75 36.5175 39.75 37.5105C39.75 38.5035 39.3965 39.35 38.6895 40.05C37.9825 40.75 37.1325 41.1 36.1395 41.1ZM0 4.1V0H6.75L14.95 17.6H33.1855L40.9 4.2H45.6L37.3 19.6C36.8667 20.3 36.3248 20.875 35.6745 21.325C35.0242 21.775 34.2803 22 33.443 22H14.65L12.2 26.65H40.3V30.75H11.65C10.1613 30.75 9.03917 30.1583 8.2835 28.975C7.52783 27.7917 7.48333 26.5833 8.15 25.35L11.2 19.75L4.1 4.1H0Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </div>
            </div>
            <div
              className="upanddown2"
              style={{
                position: "fixed",
                bottom: "15px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "50vw",
                height: "65px",
                backgroundColor: "#001E3ACD",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4.5vh",
                boxShadow:
                  "inset 0px 0px 5vh #001E3A, 0px 0px 5vh #FFFFFF, 0px 0px 25px #FFFFFF99",
                fontSize: "21px",
                fontWeight: 600,
                lineHeight: "7vh",
                color: "#FFFFFF",
                textShadow: "0px 0px 5vh #001E3A, 0px 0px 5px #001E3A",
                overflow: "hidden",
                zIndex: 900,
              }}
              onClick={(e) => {e.stopPropagation(); setIsOpen(!isOpen);}}
            >
              {/* Wishlist&nbsp;{cart?.length > 0 && <span style={{ fontSize: '18px', fontWeight: 300, lineHeight: '18px', color: '#001E3A', textShadow: '0px 0px 50px #ffffff85' }}>({cart.length})</span>} */}
              Cart
              <div
                style={{
                  position: "relative",
                  top: "-8px",
                  left: "7px",
                  width: "30px",
                  height: "35px",
                  marginRight: "10px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    position: "absolute",
                    top: "-11px",
                    left: "15px",
                    textAlign: "center",
                    transform: "translateX(-50%)",
                  }}
                >
                  {cart.length}
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="27"
                  viewBox="0 0 46 42"
                  fill="none"
                >
                  <path
                    d="M11.9395 41.1C10.9465 41.1 10.1 40.7465 9.4 40.0395C8.7 39.3325 8.35 38.4825 8.35 37.4895C8.35 36.4965 8.7035 35.65 9.4105 34.95C10.1175 34.25 10.9675 33.9 11.9605 33.9C12.9535 33.9 13.8 34.2535 14.5 34.9605C15.2 35.6675 15.55 36.5175 15.55 37.5105C15.55 38.5035 15.1965 39.35 14.4895 40.05C13.7825 40.75 12.9325 41.1 11.9395 41.1ZM36.1395 41.1C35.1465 41.1 34.3 40.7465 33.6 40.0395C32.9 39.3325 32.55 38.4825 32.55 37.4895C32.55 36.4965 32.9035 35.65 33.6105 34.95C34.3175 34.25 35.1675 33.9 36.1605 33.9C37.1535 33.9 38 34.2535 38.7 34.9605C39.4 35.6675 39.75 36.5175 39.75 37.5105C39.75 38.5035 39.3965 39.35 38.6895 40.05C37.9825 40.75 37.1325 41.1 36.1395 41.1ZM0 4.1V0H6.75L14.95 17.6H33.1855L40.9 4.2H45.6L37.3 19.6C36.8667 20.3 36.3248 20.875 35.6745 21.325C35.0242 21.775 34.2803 22 33.443 22H14.65L12.2 26.65H40.3V30.75H11.65C10.1613 30.75 9.03917 30.1583 8.2835 28.975C7.52783 27.7917 7.48333 26.5833 8.15 25.35L11.2 19.75L4.1 4.1H0Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </div>
              {/* &nbsp;<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FFFFFF"><path d="m480-560-56-56 63-64H320v-80h167l-64-64 57-56 160 160-160 160ZM280-80q-33 0-56.5-23.5T200-160q0-33 23.5-56.5T280-240q33 0 56.5 23.5T360-160q0 33-23.5 56.5T280-80Zm400 0q-33 0-56.5-23.5T600-160q0-33 23.5-56.5T680-240q33 0 56.5 23.5T760-160q0 33-23.5 56.5T680-80ZM40-800v-80h131l170 360h280l156-280h91L692-482q-11 20-29.5 31T622-440H324l-44 80h480v80H280q-45 0-68.5-39t-1.5-79l54-98-144-304H40Z" /></svg>&nbsp;{cart.length} */}
            </div>
          </>
        )
      )}
    </>
  );
};

export default Cart;
